// deployment_class: customer_env_allowed
/**
 * PortalSwitcher — dropdown lists portals the user has access to. Plan 14
 * §Task 6 + Spec 12 §5.1.
 *
 * Click a portal entry → navigate to its home path. Currently-active
 * portal is highlighted. Hidden when only the marketplace is visible
 * (single-portal users see no switcher).
 */

import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { TemplateMenu } from './TemplateMenu';
import { useExclusiveMenu } from './useExclusiveMenu';
import { computeVisiblePortals } from '../utils/visible-portals';
import type { PortalId } from '../utils/visible-portals';
import {
  getPortalFromPath,
  getPortalHomePath,
} from '../utils/route-portal-mapping';

interface PortalEntry {
  id: PortalId;
  label: string;
}

const ALL_PORTALS: PortalEntry[] = [
  { id: 'dashboard', label: 'Executive Dashboard' },
  { id: 'marketplace', label: 'Marketplace' },
  { id: 'partnership', label: 'Partnership' },
  { id: 'sales', label: 'Sales' },
  { id: 'data-analytics', label: 'Data Analytics' },
];

export function PortalSwitcher(): JSX.Element | null {
  const { status, scopes, organizations } = useAuth();
  const location = useLocation();
  const { open, toggle, close, containerRef, triggerRef } = useExclusiveMenu('portal');

  const visible = computeVisiblePortals({
    scopes,
    organizations,
    authenticated: status === 'authenticated',
  });

  const accessible = ALL_PORTALS.filter((p) => isVisible(visible, p.id));
  // Hide entirely if only marketplace is accessible — no useful switcher
  if (accessible.length <= 1) return null;

  const currentPortal = getPortalFromPath(location.pathname);
  // On a route that belongs to no portal (e.g. /projects/new) there is nothing
  // to name — the button reads "Portal" once rather than "Portal: Portal".
  const currentLabel = accessible.find((p) => p.id === currentPortal)?.label ?? null;

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="px-3 py-1 border border-rule bg-paper-soft text-body-s hover:bg-paper-amber focus:outline-none focus:ring-2 focus:ring-trust"
      >
        {currentLabel === null ? (
          <span className="font-medium">Portal</span>
        ) : (
          <>
            Portal: <span className="font-medium">{currentLabel}</span>
          </>
        )}
        <span className="ml-1" aria-hidden="true">
          ▼
        </span>
      </button>
      {open && (
        // One panel holding two groups: the portal listbox, and — for admins
        // only — the template actions beneath it. The panel is the positioned
        // element so both share it; the listbox stays a pure listbox, because
        // it must contain only options.
        <div className="kd-portalpanel absolute right-0 mt-1 min-w-[220px] z-10">
        <div
          role="listbox"
          aria-label="Portal switcher"
        >
          {accessible.map((entry) => {
            const isCurrent = entry.id === currentPortal;
            return (
              <Link
                key={entry.id}
                role="option"
                aria-selected={isCurrent}
                aria-current={isCurrent ? 'page' : undefined}
                to={getPortalHomePath(entry.id)}
                onClick={close}
                className={
                  'block px-3 py-2 text-body-s hover:bg-paper-soft focus:outline-none focus:bg-paper-amber ' +
                  (isCurrent ? 'bg-paper-blue text-trust font-medium' : '')
                }
              >
                {entry.label}
              </Link>
            );
          })}
        </div>
        <TemplateMenu onDone={close} />
        </div>
      )}
    </div>
  );
}

function isVisible(
  visible: ReturnType<typeof computeVisiblePortals>,
  id: PortalId,
): boolean {
  switch (id) {
    case 'dashboard':
      return visible.dashboard;
    case 'marketplace':
      return visible.marketplace;
    case 'partnership':
      return visible.partnership;
    case 'sales':
      return visible.sales;
    case 'data-analytics':
      return visible.dataAnalytics;
  }
}
